import React, { useEffect, useState } from 'react';
import { Form, message, Spin } from 'antd';
import DashboardLayout from '../../Dashboard/DashboardLayout';
import auctionService from '../../../services/auctionService';
import WizardAuctionStep from './WizardAuctionStep';
import WizardLotsStep from './WizardLotsStep';
import WizardDoneStep from './WizardDoneStep';
import { computeMinIncrement } from '../../../utils/pricing';

const STEPS = [
    { key: 0, label: 'Auction Info' },
    { key: 1, label: 'Add Lots' },
    { key: 2, label: 'Done' }
];

const StepProgress = ({ current }) => (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 32 }}>
        {STEPS.map((step) => (
            <div key={step.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div
                    style={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: 15,
                        color: step.key <= current ? '#fff' : '#8c8c8c',
                        background: step.key <= current ? '#1890ff' : '#f0f0f0'
                    }}
                >
                    {step.key + 1}
                </div>
                {step.key < STEPS.length - 1 && (
                    <div style={{ width: 40, height: 3, background: step.key < current ? '#1890ff' : '#f0f0f0' }} />
                )}
            </div>
        ))}
    </div>
);

const EasyAuctionWizard = () => {
    const [form] = Form.useForm();
    const [step, setStep] = useState(0);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [categories, setCategories] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [auctionPhotos, setAuctionPhotos] = useState([]);
    const [creatingAuction, setCreatingAuction] = useState(false);
    const [auction, setAuction] = useState(null);
    const [lots, setLots] = useState([]);
    const [addingLot, setAddingLot] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const response = await auctionService.getCategories();
                setCategories(response.data || []);
            } catch (error) {
                message.error('Could not load categories. Please refresh the page.');
            } finally {
                setLoadingCategories(false);
            }
        })();
    }, []);

    const handleAuctionNext = async (values) => {
        if (!selectedCategoryId) return;

        setCreatingAuction(true);
        try {
            const payload = {
                title: values.title,
                description: values.description,
                category_id: selectedCategoryId,
                start_time: values.start_time.toISOString(),
                location: values.location || null,
                video_url: values.video_url || null,
                is_private: false,
                is_sequential: false,
                lot_close_time_seconds: 60
            };
            const response = await auctionService.createAuction(payload);
            const createdAuction = response.data;
            setAuction(createdAuction);

            if (auctionPhotos.length > 0) {
                try {
                    const formData = new FormData();
                    auctionPhotos.forEach((file) => formData.append('images[]', file));
                    await auctionService.uploadAuctionImages(createdAuction.slug, formData);
                } catch (imageError) {
                    message.warning('Auction created, but photos could not be uploaded.');
                }
            }

            setStep(1);
        } catch (error) {
            message.error(error.response?.data?.message || 'Could not create the auction. Please try again.');
        } finally {
            setCreatingAuction(false);
        }
    };

    const handleAddLot = async ({ title, description, starting_price, quantity, video_url, default_increment, photos }) => {
        setAddingLot(true);
        try {
            const lotPayload = {
                title,
                description,
                starting_price,
                quantity,
                video_url: video_url || null,
                default_increment: default_increment || computeMinIncrement(starting_price)
            };
            const createResponse = await auctionService.createLot(auction.slug, lotPayload);
            const createdLot = createResponse.data;

            if (photos && photos.length > 0) {
                const formData = new FormData();
                photos.forEach((file) => formData.append('images[]', file));
                await auctionService.uploadLotImages(auction.slug, createdLot.slug, formData);
            }

            setLots((prev) => [
                ...prev,
                {
                    id: createdLot.id,
                    slug: createdLot.slug,
                    title,
                    starting_price,
                    quantity,
                    previewUrl: photos && photos.length > 0 ? URL.createObjectURL(photos[0]) : null
                }
            ]);
            message.success('Lot added!');
            return true;
        } catch (error) {
            message.error(error.response?.data?.message || 'Could not add this lot. Please try again.');
            return false;
        } finally {
            setAddingLot(false);
        }
    };

    const handleDeleteLot = async (lot) => {
        try {
            await auctionService.deleteLot(auction.slug, lot.slug);
            setLots((prev) => prev.filter((l) => l.id !== lot.id));
        } catch (error) {
            message.error('Could not remove this lot. Please try again.');
        }
    };

    return (
        <DashboardLayout className="easy-auction-wizard">
            <div style={{ maxWidth: 720, margin: '0 auto', padding: '16px 8px 48px' }}>
                <StepProgress current={step} />

                {step === 0 && (
                    <Spin spinning={loadingCategories}>
                        <WizardAuctionStep
                            form={form}
                            categories={categories}
                            selectedCategoryId={selectedCategoryId}
                            onSelectCategory={setSelectedCategoryId}
                            loading={creatingAuction}
                            onNext={handleAuctionNext}
                            photos={auctionPhotos}
                            onPhotosChange={setAuctionPhotos}
                        />
                    </Spin>
                )}

                {step === 1 && auction && (
                    <WizardLotsStep
                        auctionTitle={auction.title}
                        lots={lots}
                        addingLot={addingLot}
                        onAddLot={handleAddLot}
                        onDeleteLot={handleDeleteLot}
                        onFinish={() => setStep(2)}
                    />
                )}

                {step === 2 && auction && (
                    <WizardDoneStep
                        auctionSlug={auction.slug}
                        auctionTitle={auction.title}
                        lotCount={lots.length}
                        onAddMore={() => setStep(1)}
                    />
                )}
            </div>
        </DashboardLayout>
    );
};

export default EasyAuctionWizard;
